import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BrickWall, Download, FlipVertical2, Layers, Pause, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getThemeContext } from '../../../../components/providers/ThemeProvider.jsx';
import { showErrorAlert } from '../../../../components/HelperAlerts/HelpersAlerts.jsx';

/**
 * Modal de opciones previas a la descarga del 3MF (admin).
 *
 * Props:
 *  - keyring   : el modelo guardado con { id, name }
 *  - onClose   : cierra el modal sin descargar
 */
function Download3mfModal({ keyring, onClose }) {
    const theme = getThemeContext();
    const dialogRef = useRef(null);
    const [spreadPlates, setSpreadPlates] = useState(false);
    const [groupByColor, setGroupByColor] = useState(false);
    const [insertPause, setInsertPause] = useState(true);
    const [pauseCustom, setPauseCustom] = useState(false);
    const [faceDown, setFaceDown] = useState(true);
    const [brickLayers, setBrickLayers] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // El filo del abrelatas tiene que ser la primera capa: boca arriba, la
    // cuña sale como un voladizo y el labio de la ranura como un puente fino.
    const hasCanOpener = /insert_\d+_abrelatas_/i.test(keyring?.svg_2d || '');

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        dialog.showModal();
        return () => { if (dialog.open) dialog.close(); };
    }, []);

    const handleBackdropClick = (e) => {
        if (e.target === dialogRef.current) onClose();
    };

    const handleDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            const params = {};
            if (spreadPlates) params.spread_plates = 1;
            if (spreadPlates && groupByColor) params.group_by_color = 1;
            if (insertPause) params.insert_pause = 1;
            if (insertPause && pauseCustom) params.pause_custom = 1;
            if (!faceDown) params.flip_face_down = 0;
            if (brickLayers) params.brick_layers = 1;
            const response = await axios.get(
                `/customized-keyrings/${keyring.id}/3mftoken/`,
                { params, responseType: 'blob' },
            );

            const disposition = response.headers['content-disposition'];
            const safeName = (keyring.name || 'modelo').replace(/[\\/:*?"<>|]/g, '_');
            let fileName = `keyring_${safeName}_${keyring.id}${spreadPlates ? '_spread' : ''}.3mf`;
            const match = disposition?.match(/filename="?([^"]+)"?/);
            if (match?.[1]) fileName = match[1];

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('3MF descargado correctamente', {
                theme: theme === 'dark' ? 'dark' : 'light',
            });
            onClose();
        } catch (error) {
            console.error('Error al descargar 3MF:', error);
            await showErrorAlert({
                text: error.response?.status === 404
                    ? 'El archivo 3MF no se encontró en el servidor.'
                    : 'Hubo un error al descargar el archivo 3MF.',
                theme,
            });
        } finally {
            setDownloading(false);
        }
    };

    return createPortal(
        <dialog
            ref={dialogRef}
            className="modal"
            onClick={handleBackdropClick}
        >
            <div className="modal-box max-w-sm">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">Descargar 3MF</h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-circle btn-ghost"
                        onClick={onClose}
                        aria-label="Cerrar"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <p className="text-sm text-base-content/70 mb-5">
                    Elige cómo quieres exportar el proyecto antes de descargarlo.
                </p>

                <div className="flex flex-col gap-3">
                    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3 hover:bg-base-200 transition-colors">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-primary mt-0.5"
                            checked={spreadPlates}
                            onChange={(e) => setSpreadPlates(e.target.checked)}
                        />
                        <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-1.5 font-medium text-sm">
                                <Layers className="h-4 w-4 text-primary" />
                                Distribuir en varias camas
                            </span>
                            <span className="text-xs text-base-content/60">
                                Cada pieza se abre en su propia cama de Orca. Útil cuando el
                                diseño tiene varias partes y quieres imprimirlas por separado.
                            </span>
                        </div>
                    </label>

                    {spreadPlates && (
                        <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3 hover:bg-base-200 transition-colors ml-4">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary mt-0.5"
                                checked={groupByColor}
                                onChange={(e) => setGroupByColor(e.target.checked)}
                            />
                            <div className="flex flex-col gap-0.5">
                                <span className="font-medium text-sm">Priorizar mismo color por cama</span>
                                <span className="text-xs text-base-content/60">
                                    Agrupa piezas del mismo color en la misma cama para reducir
                                    cambios de filamento y torres de purga.
                                </span>
                            </div>
                        </label>
                    )}

                    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3 hover:bg-base-200 transition-colors">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-primary mt-0.5"
                            checked={insertPause}
                            onChange={(e) => setInsertPause(e.target.checked)}
                        />
                        <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-1.5 font-medium text-sm">
                                <Pause className="h-4 w-4 text-primary" />
                                Pausa para imanes, NFC y arandelas
                            </span>
                            <span className="text-xs text-base-content/60">
                                Añade la pausa de Orca justo antes de tapar cada cavidad, para
                                colocar el imán, el NFC o la arandela del abrebotellas. Solo
                                afecta si el diseño los lleva.
                            </span>
                        </div>
                    </label>

                    {insertPause && (
                        <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3 hover:bg-base-200 transition-colors ml-4">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-secondary mt-0.5"
                                checked={pauseCustom}
                                onChange={(e) => setPauseCustom(e.target.checked)}
                            />
                            <div className="flex flex-col gap-0.5">
                                <span className="font-medium text-sm">Gcode personalizado (experimental)</span>
                                <span className="text-xs text-base-content/60">
                                    Sustituye la pausa por una con melodía, que sube el cabezal y
                                    baja la temperatura mientras esperas. Sin probar a fondo.
                                </span>
                            </div>
                        </label>
                    )}

                    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3 hover:bg-base-200 transition-colors">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-primary mt-0.5"
                            checked={brickLayers}
                            onChange={(e) => setBrickLayers(e.target.checked)}
                        />
                        <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-1.5 font-medium text-sm">
                                <BrickWall className="h-4 w-4 text-primary" />
                                Brick layers (experimental)
                            </span>
                            <span className="text-xs text-base-content/60">
                                Desplaza media capa las paredes alternas para que encajen como
                                ladrillos y aguanten más. Deja las paredes en Clásico y sin ajuste
                                de arcos, pero Orca no acepta scripts dentro de un 3MF: al abrirlo,
                                elige el preset de proceso «LlaverosDetalle2 BrickLayers» o pega la
                                línea del script en Otros → Scripts de posprocesado. Se aplica al
                                exportar o enviar el gcode, no en la vista previa.
                            </span>
                        </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-base-300 p-3 hover:bg-base-200 transition-colors">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-primary mt-0.5"
                            checked={faceDown}
                            onChange={(e) => setFaceDown(e.target.checked)}
                        />
                        <div className="flex flex-col gap-0.5">
                            <span className="flex items-center gap-1.5 font-medium text-sm">
                                <FlipVertical2 className="h-4 w-4 text-primary" />
                                Cara decorada contra la cama
                            </span>
                            <span className="text-xs text-base-content/60">
                                Imprime el llavero boca abajo para que el dibujo salga liso.
                                Desmarcado, se imprime con la cara decorada hacia arriba.
                            </span>
                            {hasCanOpener && !faceDown && (
                                <span className="mt-1 rounded bg-warning/15 px-2 py-1 text-xs text-warning">
                                    El diseño lleva abrelatas: boca arriba su filo no apoya en la
                                    cama y sale colgando. Déjalo marcado.
                                </span>
                            )}
                        </div>
                    </label>

                    {!spreadPlates && (
                        <p className="text-xs text-base-content/50 px-1">
                            Sin marcar: todas las piezas van en la misma cama.
                        </p>
                    )}
                </div>

                <div className="modal-action mt-6">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={onClose}
                        disabled={downloading}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleDownload}
                        disabled={downloading}
                    >
                        {downloading ? (
                            <span className="loading loading-spinner loading-xs" />
                        ) : (
                            <Download className="h-4 w-4" />
                        )}
                        Descargar
                    </button>
                </div>
            </div>
        </dialog>,
        document.body,
    );
}

export default Download3mfModal;
